'use client';

import Link from 'next/link';
import { useId, useState } from 'react';

import PlanesClinica from './PlanesClinica';
import PlanesIndividuales from './PlanesIndividuales';
import SelectorTipoPlan from './SelectorTipoPlan';
import SellosPrecios from './SellosPrecios';
import { CABECERA_SECCION_PRECIOS, TITULO_OCULTO_PLANES, type VistaPrecios } from './textosSeccionPrecios';

interface PropiedadesSeccionPrecios {
    /** La landing lleva antetítulo, h2, entradilla y la píldora a /precios; /precios ya tiene su h1 y no los repite. */
    cabecera: boolean;
    /** Ancla de la sección (la landing conserva `#precios`, D-10). */
    idSeccion?: string;
}

function CabeceraSeccion({ idTitulo }: { idTitulo: string }) {
    return (
        <div className="flex flex-col items-start gap-3.5 text-left lg:items-center lg:text-center">
            <span className="text-[12px] font-bold uppercase tracking-[0.08em] text-primary lg:text-ds-100">
                {CABECERA_SECCION_PRECIOS.antetitulo}
            </span>
            <h2
                id={idTitulo}
                className="m-0 max-w-[860px] font-display text-ds-700 font-bold leading-[1.1] tracking-[-0.02em] lg:text-[46px]"
            >
                {CABECERA_SECCION_PRECIOS.titulo}
            </h2>
            <p className="m-0 max-w-[720px] text-ds-400 leading-normal text-ink/90 lg:text-[19px]">
                {CABECERA_SECCION_PRECIOS.entradilla}
            </p>
        </div>
    );
}

function PildoraDetalle() {
    return (
        <Link
            href={CABECERA_SECCION_PRECIOS.enlace.href}
            className="box-border flex min-h-12 items-center gap-2 self-center rounded-full border-2 border-ink bg-surface-2 px-[22px] text-ds-300 font-bold text-ink no-underline shadow-ds-2"
        >
            {CABECERA_SECCION_PRECIOS.enlace.texto}
            <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
            >
                <path d="M3 8h10M9 4l4 4-4 4" />
            </svg>
        </Link>
    );
}

/**
 * La sección de precios compartida por la landing y /precios (Seccion_Precios, D-18). Es la única
 * isla de cliente del bloque (D-07): guarda qué juego de tarjetas se ve, y las tarjetas son
 * componentes normales que pinta dentro. Un solo árbol responsive: el lienzo móvil solo apila.
 */
export default function SeccionPrecios({ cabecera, idSeccion }: PropiedadesSeccionPrecios) {
    const [vista, setVista] = useState<VistaPrecios>('individual');
    const idBase = useId();
    const idTitulo = `${idBase}-titulo`;

    return (
        <section
            id={idSeccion}
            aria-labelledby={idTitulo}
            className="relative scroll-mt-28 px-4 pb-14 pt-12 text-ds-200 leading-[1.45] text-ink lg:px-8 lg:pb-[104px] lg:pt-24 xl:px-20"
        >
            <div className="mx-auto flex max-w-[1120px] flex-col gap-7 lg:gap-10">
                {/* /precios ya trae su h1: sin cabecera visible, un h2 solo para lectores de pantalla evita
                    el salto de h1 a h3 y da nombre a la sección (IN-08). Es absoluto, así que no ocupa hueco. */}
                {cabecera ? (
                    <CabeceraSeccion idTitulo={idTitulo} />
                ) : (
                    <h2 id={idTitulo} className="sr-only">
                        {TITULO_OCULTO_PLANES}
                    </h2>
                )}
                <SelectorTipoPlan vista={vista} alCambiar={setVista} />
                {vista === 'individual' ? <PlanesIndividuales idBase={idBase} /> : <PlanesClinica idBase={idBase} />}
                <SellosPrecios vista={vista} />
                {cabecera && <PildoraDetalle />}
            </div>
        </section>
    );
}
